import { DeleteOutlined, HolderOutlined } from '@ant-design/icons';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

// Modifier inline para restringir drag al eje Y (evita agregar
// dependencia @dnd-kit/modifiers solo por esto).
const restrictToVerticalAxis = ({ transform }) => ({ ...transform, x: 0 });
import { CSS } from '@dnd-kit/utilities';
import { Button, Input, Popconfirm, Select, Space, Table } from 'antd';
import React, { useMemo, useRef } from 'react';
import { AVAILABLE_ICONS, resolveIcon } from './clientTabsRegistry';

const SortableRow = ({ children, ...props }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props['data-row-key'],
  });

  const style = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { zIndex: 9999, position: 'relative' } : {}),
  };

  return React.cloneElement(
    <tr {...props} ref={setNodeRef} style={style} />,
    {},
    React.Children.map(children, (child) => {
      if (child?.key === 'drag-handle') {
        return React.cloneElement(child, {
          children: (
            <Button
              type="text"
              size="small"
              icon={<HolderOutlined />}
              style={{ cursor: 'grab', touchAction: 'none' }}
              {...attributes}
              {...listeners}
              aria-label="Reordenar"
            />
          ),
        });
      }
      return child;
    })
  );
};

// Tabla reutilizable para los drawers de configuración de client_tabs.
// Props:
//   rows: array de client_tabs ordenados por order.
//   savingId: id del row que está sync en backend (desactiva inputs).
//   onEditField(row, field, value): commit de label/icon.
//   onDelete(row): borrar row.
//   onReorder(reorderedRows): se llama con el array completo en el orden nuevo;
//     el caller es responsable de persistir (endpoint reorder) y refrescar.
export default function SortableTabsTable({ rows, savingId, onEditField, onDelete, onReorder }) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    })
  );

  // Preserva la referencia para evitar re-bind excesivo dentro del DnD.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const current = rowsRef.current;
    const oldIndex = current.findIndex((r) => r.id === active.id);
    const newIndex = current.findIndex((r) => r.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(current, oldIndex, newIndex);
    onReorder(next);
  };

  const columns = useMemo(
    () => [
      {
        key: 'drag-handle',
        title: '',
        width: 40,
        render: () => null, // el <Button /> lo inyecta SortableRow
      },
      {
        title: 'Key',
        dataIndex: 'key',
        width: 140,
        render: (v) => <code>{v}</code>,
      },
      {
        title: 'Label',
        dataIndex: 'label',
        render: (value, row) => (
          <Input
            defaultValue={value}
            disabled={savingId === row.id}
            onBlur={(e) => onEditField(row, 'label', e.target.value)}
            onPressEnter={(e) => e.target.blur()}
          />
        ),
      },
      {
        title: 'Ícono',
        dataIndex: 'icon',
        width: 200,
        render: (value, row) => (
          <Select
            value={value ?? undefined}
            style={{ width: '100%' }}
            allowClear
            disabled={savingId === row.id}
            onChange={(next) => onEditField(row, 'icon', next ?? null)}
            options={AVAILABLE_ICONS.map((name) => ({
              value: name,
              label: (
                <Space>
                  {resolveIcon(name)}
                  <span>{name}</span>
                </Space>
              ),
            }))}
          />
        ),
      },
      {
        title: 'Acciones',
        key: 'actions',
        width: 80,
        render: (_, row) => (
          <Popconfirm
            title={`Eliminar "${row.key}"?`}
            okText="Sí"
            cancelText="No"
            onConfirm={() => onDelete(row)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        ),
      },
    ],
    [savingId, onEditField, onDelete]
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={rows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
        <Table
          rowKey="id"
          dataSource={rows}
          columns={columns}
          pagination={false}
          size="small"
          components={{ body: { row: SortableRow } }}
          locale={{ emptyText: 'Sin tabs. Agregá el primero con el botón de arriba.' }}
        />
      </SortableContext>
    </DndContext>
  );
}
